import { useCallback, useLayoutEffect, useRef } from 'react';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import { useIsNewDesign } from './filter-bar/useIsNewDesign';

/**
 * The `getRowHref` / `onRowNavigate` pair a `DataTable` takes, which the engine cannot build itself since it
 * renders without a Router or a store: the row target in the new design only, so a classic row stays inert,
 * and the navigation that sets `returnTo`. That navigation changes identity on every location change, and
 * the table body is memoised on it, so the callback handed over keeps one identity and calls the latest.
 */
export function useRowLink<D>(getRowHref: ((row: D) => string | undefined) | undefined) {
    const isNewDesign = useIsNewDesign();
    const navigateWithReturn = useNavigateWithReturnState();
    const navigateRef = useRef(navigateWithReturn);
    useLayoutEffect(() => {
        navigateRef.current = navigateWithReturn;
    }, [navigateWithReturn]);
    const onRowNavigate = useCallback((href: string) => navigateRef.current(href), []);
    return { getRowHref: isNewDesign ? getRowHref : undefined, onRowNavigate };
}
